import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { PinLabel, InOutRetrievalService, TableDataPointInput, TableDataPointOutput } from 'src/app/services/apiHandler/i-o-retrieval/in-out-retrieval.service';

@Component({
  selector: 'app-in-out',
  templateUrl: './in-out.component.html',
  styleUrls: ['./in-out.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InOutComponent implements OnInit, AfterViewInit, OnDestroy {

  toggle_in: boolean = false;
  toggle_out: boolean = false;
  pins_in: string[] = [];
  pins_out: string[] = [];
  allInputSelected: boolean = false;
  allOutputSelected: boolean = false;
  checkedPinsIn: string [] = [];
  checkedPinsOut: string [] = [];
  inputSelected: boolean = false;
  outputSelected: boolean = false;
  pinsData!: PinLabel;
  triggered: boolean = false;
  expandIn: boolean = false;
  expandOut: boolean = false;
  loading: boolean = false;
  loadingAll: boolean = false;
  subscription: any;
  timer: any;
  frequency: number = 0;
  pinsLoaded: boolean = false;

  @Input() ecuId!: number;
  @Input() expanded!: boolean;

  displayedColumnsInput: string[] = ["id", "label", "di", "aiv", "ri", "fi", "aic"];
  displayedColumnsOutput: string[] = ["id", "label", "po", "do", "aov"];
  tableInput: MatTableDataSource<TableDataPointInput> = new MatTableDataSource<TableDataPointInput>;
  tableOutput: MatTableDataSource<TableDataPointOutput> = new MatTableDataSource<TableDataPointOutput>;
  @ViewChildren(MatTable) table: QueryList<MatTable<any>> | undefined;
  newTableDataIn: MatTableDataSource<TableDataPointInput> = new MatTableDataSource<TableDataPointInput>;
  newTableDataOut: MatTableDataSource<TableDataPointOutput> = new MatTableDataSource<TableDataPointOutput>;

  @ViewChild('sortInput') sortInput!: MatSort;
  @ViewChild('sortOutput') sortOutput!: MatSort;

  constructor(private ioService: InOutRetrievalService, private ref:ChangeDetectorRef) {}

  ngOnInit(): void {
    // fetch specific pins_data with label for ecu_id
    this.ioService.getPinLabels().subscribe((res) => {
      let allEcus: PinLabel[] =  JSON.parse(JSON.stringify(res.body));
      this.pinsData = allEcus.find(el => el.ecu_id === this.ecuId)!;
    });    
  }

  ngAfterViewInit() {
    this.tableInput.sort = this.sortInput;
    this.tableOutput.sort = this.sortOutput;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('expanded' in changes) {
      this.expanded = changes['expanded'].currentValue;      
      if(!this.expanded){
        this.triggered = false;
        this.subscription?.unsubscribe();
      } else {
        if(this.checkedPinsIn.length > 0 || this.checkedPinsOut.length > 0){
          this.updateTableData();
        } else {
          this.initData();
          this.timer = setTimeout(()=>{
            this.loadingAll = true;            
            this.ref.detectChanges();
          },1500);
        }
      }
    }
  }

  // fetch specific data for ecu_id - initial data 
  private initData() {
    this.subscription = this.ioService.getPinData(this.ecuId).subscribe((res) => {
      this.loadingAll = false;
      this.ref.detectChanges();
      clearTimeout(this.timer);
      let data = JSON.parse(JSON.stringify(res.body));
      if(res.body?.toString().includes('Error')) {
        this.loadingAll = true;
        this.ref.detectChanges();
        this.initData();
      } else {
        if(Object.keys(data).length === 0) {
          this.initData();
        }
        for(let pin of data!.input_pins) {
          this.pins_in.push(pin.id);
        }
        for(let pin of data!.output_pins) {
          this.pins_out.push(pin.id);
        }
        this.pinsLoaded = true;
        this.ref.detectChanges();
      }
    });
  }
	
  /**
   * data gets fetched -> after answer -> it's triggered again
   * update table if something new is selected, lock until new answer is fetched - until then the old data can still be used
   * display the retrieval speed
   */
  updateTableData(){
    this.newTableDataIn.data = [];
    this.newTableDataOut.data = [];
    
    if(!this.triggered){
      this.triggered = true;
      this.timer = setTimeout(()=>{
        this.loading = true;
        this.ref.detectChanges();
      },1500);
      this.fetchData();
    }
  }

  private fetchData() {
    let startFrom = new Date().getTime();
    this.subscription = this.ioService.getPinData(this.ecuId).subscribe((res) => {
      this.frequency = new Date().getTime() - startFrom;      
      this.loading = false;
      clearTimeout(this.timer);
      if(res.body?.toString().includes('Error')) {
        this.triggered = false;
        this.updateTableData();
      } else {
        let data = JSON.parse(JSON.stringify(res.body));
        // update input table with data of selected pins
        for(let pin of data!.input_pins) {
          if(this.checkedPinsIn.includes(pin.id)){
            let label = this.pinsData.pins.find(el => el.id === pin.id)?.label;
            if(label == undefined) {
              label = "";
            }
            let digIn = this.digitalToBin(pin.di);
            let item: TableDataPointInput = {
              "id": pin.id,
              "label": label,
              "di": digIn,
              "aiv": pin.aiv,
              "ri": pin.ri,
              "fi": pin.fi,
              "aic": pin.aic
            };
            this.newTableDataIn.data.push(item);
          }
        };

        // update output table with data of selected pins
        for(let pin of data!.output_pins) {
          if(this.checkedPinsOut.includes(pin.id)){
            let label = this.pinsData.pins.find(el => el.id === pin.id)?.label;
              if(label == undefined) {
                label = "";
              }
            let digOut = this.digitalToBin(pin.do);
            let item: TableDataPointOutput = {
              "id": pin.id,
              "label": label,
              "po": pin.po,
              "do": digOut,
              "aov": pin.aov
            };
            this.newTableDataOut.data.push(item);
          }
        };
        // INPUT - handle when data-arra empty
        if(this.newTableDataIn.data.length === 0 && this.checkedPinsIn.length === 0) {
          this.tableInput.data = this.newTableDataIn.data;
        } else if(this.newTableDataIn.data.length != 0) {
          this.tableInput.data = this.newTableDataIn.data;
        }
        // OUTPUT - handle when data-arra empty
        if(this.newTableDataOut.data.length === 0 && this.checkedPinsOut.length === 0) {
          this.tableOutput.data = this.newTableDataOut.data;
        } else if(this.newTableDataOut.data.length != 0) {
          this.tableOutput.data = this.newTableDataOut.data;
        }
        
        this.tableInput.sort = this.sortInput;
        this.tableOutput.sort = this.sortOutput;
        this.table?.first?.renderRows();
        this.table?.last?.renderRows();
        this.triggered = false;
        this.updateTableData();
      }
    });
  }

  private digitalToBin(pin: any) {
    let digital: number;
    if(pin === "ON") {
      digital = 1;                
    } else if(pin === "OFF") {
      digital = 0;
    } else {
      digital = pin;
    }
    return digital;
  }

  public applyFilterInput(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.tableInput.filterPredicate = function (data, filter: string): boolean {
        return (
            data.id.toLowerCase().includes(filter.toLowerCase()) ||
            data.label.toLowerCase().includes(filter.toLowerCase())
        );
    };
    if(filterValue.trim().toLowerCase() == '') {
      this.tableInput.filter = filterValue.trim().toLowerCase();
      this.updateTableData();
    } else {
      this.tableInput.filter = filterValue.trim().toLowerCase();
    }
  }

  public applyFilterOutput(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.tableOutput.filterPredicate = function (data, filter: string): boolean {
        return (
            data.id.toLowerCase().includes(filter.toLowerCase()) ||
            data.label.toLowerCase().includes(filter.toLowerCase())
        );
    };
    if(filterValue.trim().toLowerCase() == '') {
      this.tableOutput.filter = filterValue.trim().toLowerCase();
      this.updateTableData();
    } else {
      this.tableOutput.filter = filterValue.trim().toLowerCase();
    }
  }


  // INPUT - Handling

  allInputChecked(){
    this.allInputSelected = !this.allInputSelected;
    if(this.allInputSelected) {
      this.checkedPinsIn = this.pins_in;
      this.inputSelected = true;
      this.expandIn = true;
    } else {
      this.inputSelected = false;
      this.expandIn = false;
      this.checkedPinsIn = [];
      this.newTableDataIn.data = [];
      this.tableInput.data = [];
    }
	  this.updateTableData();
  }

  partiallySelectedIn(): boolean {
    return this.checkedPinsIn.length < this.pins_in.length && this.checkedPinsIn.length > 0? true : false;
  }

  allSelectedIn(): boolean {
    return this.checkedPinsIn.length == this.pins_in.length && this.pins_in.length > 0 ? true : false;
  }

  onInputSelectionChange(checkedItems: any) {
    if(checkedItems.length > 0){
      this.expandIn = true;
    } else {
      this.expandIn = false;
    }
    this.checkedPinsIn = [];
    for (let index = 0; index < checkedItems.length; index++) {
      this.checkedPinsIn.push(checkedItems[index]._value);
    }
    if(this.checkedPinsIn.length > 0){
      this.inputSelected = true;
    } else {
      this.inputSelected = false;
    }
    if(this.checkedPinsIn.length === this.pins_in.length){
      this.allInputSelected = true;
    }
    this.updateTableData();
  }


  // OUTPUT - Handling

  allOutputChecked() {
    this.allOutputSelected = !this.allOutputSelected;
    if(this.allOutputSelected) {
      this.checkedPinsOut = this.pins_out;
      this.outputSelected = true;
      this.expandOut = true
    } else {
      this.outputSelected = false;
      this.expandOut = false;
      this.checkedPinsOut = [];
      this.newTableDataOut.data = [];
      this.tableOutput.data = [];
    }
	  this.updateTableData();
  }

  partiallySelectedOut(): boolean {
    return this.checkedPinsOut.length < this.pins_out.length && this.checkedPinsOut.length > 0? true : false;
  }

  allSelectedOut(): boolean {
    return this.checkedPinsOut.length == this.pins_out.length && this.pins_out.length > 0 ? true : false;
  }

  onOutputSelectionChange(checkedItems: any) {
    if(checkedItems.length > 0){
      this.expandOut = true;
    } else {
      this.expandOut = false;
    }
    this.checkedPinsOut = [];
    for (let index = 0; index < checkedItems.length; index++) {
      this.checkedPinsOut.push(checkedItems[index]._value);
    }
    if(this.checkedPinsOut.length > 0) {
      this.outputSelected = true;
    } else {
      this.outputSelected = false;
    }
    if(this.checkedPinsOut.length === this.pins_out.length) {
      this.allOutputSelected = true;
    }
	  this.updateTableData();
  }
  
}
